import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, X, Check, Loader2, ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import PasswordInput from './PasswordInput';

/**
 * "Cambia numero di telefono" entry point + 2-step modal flow.
 * Step 1: current password + new phone → backend sends 4-digit SMS OTP.
 * Step 2: user types the code → backend applies the change.
 */
export default function ChangePhoneButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { user, setUser } = useAuth();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('request');
  const [currentPw, setCurrentPw] = useState('');
  const [newPhone, setNewPhone] = useState('+39');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep('request');
    setCurrentPw('');
    setNewPhone('+39');
    setCode('');
  };
  const close = () => { if (!saving) { setOpen(false); reset(); } };

  const requestOtp = async (e) => {
    e?.preventDefault();
    const phone = newPhone.trim();
    if (!phone.startsWith('+') || phone.length < 8) {
      toast.error(t('accountSecurity.invalidPhone', 'Numero non valido (usa il formato +39...)'));
      return;
    }
    setSaving(true);
    try {
      const res = await httpClient.post(`${API_URL}/api/users/me/change-phone/request`, {
        current_password: currentPw,
        new_phone: phone,
      });
      toast.success(res.data?.message || t('accountSecurity.codeSent', 'Codice inviato'));
      setStep('confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('accountSecurity.genericError', 'Errore'));
    } finally {
      setSaving(false);
    }
  };

  const confirmOtp = async (e) => {
    e?.preventDefault();
    if (code.length !== 4) {
      toast.error(t('accountSecurity.codeMustBe4', 'Inserisci il codice di 4 cifre'));
      return;
    }
    setSaving(true);
    try {
      const res = await httpClient.post(`${API_URL}/api/users/me/change-phone/confirm`, { code });
      const revoked = res.data?.other_sessions_revoked || 0;
      const baseMsg = res.data?.message || t('accountSecurity.phoneUpdated');
      if (revoked > 0) {
        toast.success(t('accountSecurity.updatedAndRevoked', { msg: baseMsg, n: revoked }));
      } else {
        toast.success(baseMsg);
      }
      // Backend now returns `phone` (canonical). Keep a `phone_number` fallback
      // for older API deployments that still answer with the legacy key.
      const newPhone = res.data?.phone || res.data?.phone_number;
      if (newPhone) {
        setUser((prev) => (prev ? { ...prev, phone: newPhone, phone_number: newPhone } : prev));
      }
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('accountSecurity.genericError', 'Errore'));
    } finally {
      setSaving(false);
    }
  };

  const inputBase = `w-full px-4 h-12 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 ${
    isDark
      ? 'bg-white/5 text-white border border-white/15 placeholder-white/50'
      : 'bg-gray-50 text-gray-900 border border-gray-200 placeholder-gray-400'
  }`;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="change-phone-btn"
      >
        <Phone className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium">{t('accountSecurity.changePhone', 'Cambia numero di telefono')}</div>
          <div className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {user?.phone || user?.phone_number || t('accountSecurity.changePhoneDesc', 'Aggiorna il numero di accesso')}
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={close}
          data-testid="change-phone-overlay"
        >
          <form
            onSubmit={step === 'request' ? requestOtp : confirmOtp}
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className="flex items-center gap-2">
                {step === 'confirm' && (
                  <button
                    type="button"
                    onClick={() => !saving && setStep('request')}
                    className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                    data-testid="change-phone-back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {step === 'request'
                    ? t('accountSecurity.changePhone', 'Cambia numero di telefono')
                    : t('accountSecurity.enterCode', 'Inserisci il codice')}
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                data-testid="change-phone-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {step === 'request' ? (
                <>
                  <PasswordInput
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder={t('accountSecurity.currentPassword', 'Password attuale')}
                    autoComplete="current-password"
                    autoFocus
                    className={inputBase}
                    iconColor={isDark ? 'text-white/60 hover:text-white/90' : 'text-gray-400 hover:text-gray-600'}
                    data-testid="change-phone-current-pw"
                  />
                  <input
                    type="tel"
                    inputMode="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+39 333 1234567"
                    className={inputBase}
                    data-testid="change-phone-new-phone"
                  />
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {t('accountSecurity.newPhoneHint', 'Ti invieremo un SMS con un codice di 4 cifre al nuovo numero.')}
                  </p>
                </>
              ) : (
                <>
                  <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {t('accountSecurity.codeSentTo', 'Abbiamo inviato un codice a')}{' '}
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{newPhone}</span>
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="1234"
                    autoFocus
                    className={`${inputBase} text-center font-mono text-lg tracking-[0.5em]`}
                    data-testid="change-phone-code"
                  />
                </>
              )}
            </div>

            <div className={`p-4 flex items-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={close}
                disabled={saving}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                data-testid="change-phone-cancel"
              >
                {t('common.cancel', 'Annulla')}
              </button>
              <button
                type="submit"
                disabled={
                  saving ||
                  (step === 'request' && (!currentPw || newPhone.length < 8)) ||
                  (step === 'confirm' && code.length !== 4)
                }
                className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="change-phone-submit"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : step === 'request' ? (
                  <>{t('accountSecurity.sendCode', 'Invia codice')}</>
                ) : (
                  <><Check className="w-4 h-4" /> {t('common.confirm', 'Conferma')}</>
                )}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
